import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { getCorrectSocialUrl } from '../../../../../shared/functions/Functions';
import { useTranslation } from 'react-i18next';
import { DiscoveryService } from '../service';
import { ErrorToast } from '../../../../../utils/toasters';
import { DiscoveryFavoriteProfile, DiscoveryFavoriteResponse } from '../dto/IDiscoveryFilter';

interface IDiscoveryFavoritesClientNoteModalProps {
    show: boolean;
    closeModal: () => void;
    setUpdateFavoritesList: React.Dispatch<React.SetStateAction<boolean>>;
    user?: DiscoveryFavoriteResponse;
    isClient?: boolean;
}

const DiscoveryFavoritesClientNoteModal = ({
    show,
    closeModal,
    isClient,
    user,
    setUpdateFavoritesList,
}: IDiscoveryFavoritesClientNoteModalProps) => {
    const userObj = (user?.internalUser ?? user?.externalUser ?? {}) as Partial<DiscoveryFavoriteProfile>;
    const { photoUrl = '', mainPlatform, fullName = '', title = '' } = userObj;
    const { t } = useTranslation();
    const [newNote, setNote] = useState(user?.clientNote);
    const [editedBy, setEditedBy] = useState(user?.clientEditedBy);
    const [isSubmitClicked, setIsSubmitClicked] = useState(false);

    const addClientNoteAction = () => {
        setIsSubmitClicked(true);
        if (!newNote) {
            return;
        }
        DiscoveryService.addClientNote(user?.id ?? 0, newNote ?? '')
            .then(() => {
                setUpdateFavoritesList(prevState => !prevState);
                closeModal();
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <Modal show={show} onHide={closeModal}>
            <Modal.Body>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <CustomImageHandler
                            altTag={fullName}
                            classes="user-img-rounded me-3"
                            thumbnailPath={photoUrl ?? ''}
                            photoPath={photoUrl ?? ''}
                            style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                            name={fullName ?? title}
                        />
                        <div className="mx-2">
                            <strong>{fullName ?? title}</strong>
                            <br />
                            <a
                                target="_blank"
                                href={`${getCorrectSocialUrl(mainPlatform?.platformEnum as string, title ?? '')}`}
                                className="text-muted cursor-pointer"
                                rel="noopener noreferrer"
                            >
                                @{title}
                            </a>
                        </div>
                    </div>
                    <CustomImageHandler altTag={mainPlatform?.name ?? ''} photoPath={mainPlatform?.path ?? ''} />
                </div>
                <div className="mt-3">
                    <h5>{t('brand.campaign.discovery.leave_client_note')}</h5>
                    <Form.Control
                        as="textarea"
                        name="exclusivityDescription"
                        value={newNote ?? ''}
                        disabled={!isClient}
                        onChange={event => setNote(event.target.value)}
                        rows={7}
                        maxLength={2000}
                        className={`w-100 mb-0 h-100 ${isSubmitClicked && !newNote ? 'border-danger' : ''}`}
                        placeholder="Write your note"
                    />
                </div>
                <span className="text-muted mt-3">
                    {editedBy?.displayName ? `Edited by: ${editedBy?.firstName} ${editedBy?.lastName}` : null}
                </span>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-end">
                <Button onClick={closeModal} variant="outline-primary" className="btn-sm mx-2">
                    {t('general.button.cancel')}
                </Button>
                {isClient && (
                    <Button onClick={addClientNoteAction} variant="primary" className="btn-sm">
                        {t('brand.campaign.discovery.save_client_note')}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default DiscoveryFavoritesClientNoteModal;
